import React, { useRef, useEffect, useState } from 'react';
import { Group, Circle, Text } from 'react-konva';
import Konva from 'konva';

const INPUT_COLOR = '#10b981';
const OUTPUT_COLOR = '#818cf8';

interface GhostSocketProps {
  x: number;
  y: number;
  ghostType: 'input' | 'output';
  nodeId: string;
  onGhostSocketDrop: (nodeId: string, ghostType: 'input' | 'output') => void;
}

export const GhostSocket: React.FC<GhostSocketProps> = ({
  x,
  y,
  ghostType,
  nodeId,
  onGhostSocketDrop,
}) => {
  const circleRef = useRef<Konva.Circle>(null);
  const animRef = useRef<Konva.Animation | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const baseColor = ghostType === 'input' ? INPUT_COLOR : OUTPUT_COLOR;

  useEffect(() => {
    const circle = circleRef.current;
    if (!circle) return;

    if (isHovered) {
      animRef.current?.stop();
      animRef.current = null;
      circle.opacity(0.8);
      circle.getLayer()?.batchDraw();
      return;
    }

    // Start pulse animation — writes directly to Konva node, no React re-renders
    const layer = circle.getLayer();
    let t = 0;
    const anim = new Konva.Animation((frame) => {
      if (!frame) return;
      t += frame.timeDiff / 1000;
      // Oscillate between 0.3 and 0.5 at ~1.5 Hz
      circle.opacity(0.4 + 0.1 * Math.sin(2 * Math.PI * 1.5 * t));
    }, layer as Konva.Layer);
    anim.start();
    animRef.current = anim;

    return () => {
      anim.stop();
      animRef.current = null;
    };
  }, [isHovered]);

  // Clean up animation on unmount
  useEffect(() => {
    return () => {
      animRef.current?.stop();
      animRef.current = null;
    };
  }, []);

  return (
    <Group x={x} y={y}>
      <Circle
        ref={circleRef}
        radius={5.5}
        stroke={baseColor}
        strokeWidth={1.5}
        fill="transparent"
        dash={[3, 2]}
        hitStrokeWidth={12}
        onMouseEnter={(e) => {
          setIsHovered(true);
          const container = e.target.getStage()?.container();
          if (container) container.style.cursor = 'crosshair';
        }}
        onMouseLeave={(e) => {
          setIsHovered(false);
          const container = e.target.getStage()?.container();
          if (container) container.style.cursor = 'crosshair';
        }}
        onMouseUp={(e) => {
          e.cancelBubble = true;
          e.evt.stopPropagation();
          onGhostSocketDrop(nodeId, ghostType);
        }}
        perfectDrawEnabled={false}
      />
      <Text
        text="+"
        x={-5.5}
        y={-6}
        width={11}
        height={12}
        fontSize={11}
        align="center"
        fill={baseColor}
        opacity={0.7}
        listening={false}
        perfectDrawEnabled={false}
      />
    </Group>
  );
};
